import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Table, Space, Button, message, Modal, 
  Typography, Card, Tag, Tooltip 
} from 'antd';
import { 
  EditOutlined, DeleteOutlined, HistoryOutlined,
  FileTextOutlined, ClockCircleOutlined, HomeOutlined
} from '@ant-design/icons';

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

function HistoryPage() {
  const { t } = useTranslation();
  const [subtitles, setSubtitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubtitle, setSelectedSubtitle] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subtitles/list', { // 修改这里
        credentials: 'include',
      });
      const data = await response.json();
      
      if (data.success) {
        setSubtitles(data.subtitles);
      } else {
        message.error(t('history.fetchFail'));
      }
    } catch (error) {
      console.error('获取历史记录错误:', error);
      message.error(t('history.fetchFail'));
    } finally {
      setLoading(false);
    }
  };

   // 获取单个字幕的历史记录
  const fetchSubtitleHistory = async (id) => {
    try {
      const response = await fetch(`/api/subtitles/${id}/history`, {
        credentials: 'include',
      });
      const data = await response.json();
      
      if (data.success) {
        return data.history;
      } else {
        message.error(t('history.fetchFail'));
        return [];
      }
    } catch (error) {
      console.error('获取修改历史错误:', error);
      message.error(t('history.fetchFail'));
      return [];
    }
  };

  const handleEdit = (id) => {
    navigate(`/editor/${id}`);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: t('history.confirmDeleteTitle'),
      content: t('history.confirmDeleteContent'),
      okText: t('common.ok'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await fetch(`/api/subtitles/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });

          const data = await response.json();
          if (data.success) {
            message.success(t('history.deleteSuccess'));
            setSubtitles(subtitles.filter(s => s._id !== id));
          } else {
            message.error(data.message || t('history.deleteFail'));
          }
        } catch (error) {
          console.error('删除错误:', error);
          message.error(t('history.deleteActionFail'));
        }
      },
    });
  };

  const handleViewHistory = async (subtitle) => {
    const history = await fetchSubtitleHistory(subtitle._id);
    setSelectedSubtitle({
      ...subtitle,
      history
    });
  };

  const columns = [
    {
      title: t('history.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text) => (
        <Space>
          <FileTextOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: t('history.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => (
        <Tooltip title={new Date(text).toLocaleString()}>
          <Space>
            <ClockCircleOutlined />
            <span>{new Date(text).toLocaleDateString()}</span>
          </Space>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    },
    {
      title: t('history.modifyTimes'),
      dataIndex: 'history',
      key: 'history',
      render: (history) => (
        <Tag color="blue">
          {t('history.timesTag', { count: history?.length || 0 })}
        </Tag>
      ),
    },
    {
      title: t('history.modifiedAt'),
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      render: (text) => text ? (
        <Tooltip title={new Date(text).toLocaleString()}>
          {new Date(text).toLocaleDateString()}
        </Tooltip>
      ) : '-',
    },
    {
      title: t('table.action'),
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record._id)}
          >
            {t('button.edit')}
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
          >
            {t('history.viewHistory')}
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          >
            {t('button.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="history-layout">
      <Header style={{ background: '#001529', padding: '0 24px', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: '1', display: 'flex', alignItems: 'center' }}>
          <Space align="center">
            <Button 
              icon={<HomeOutlined />} 
              onClick={() => navigate('/dashboard')}
              style={{ 
                background: '#fff',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                height: '32px'
              }}
            >
              {t('common.backHome')}
            </Button>
            <Typography.Title 
              level={4} 
              style={{ 
                margin: 0,
                color: '#fff',
                lineHeight: '32px'
              }}
            >
              {t('history.title')}
            </Typography.Title>
          </Space>
        </div>
      </Header>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Card bordered={false}>
            <Table
              columns={columns}
              dataSource={subtitles}
              rowKey="_id"
              loading={loading}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => t('history.recordsTotal', { total }),
              }}
            />
          </Card>
        </div>

        <Modal
          title={t('history.modalTitle')}
          open={!!selectedSubtitle}
          onCancel={() => setSelectedSubtitle(null)}
          footer={null}
          width={800}
        >
          {selectedSubtitle && (
            <Table
              dataSource={selectedSubtitle.history}
              columns={[
                {
                  title: t('history.changeTime'),
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (text) => new Date(text).toLocaleString(),
                },
                {
                  title: t('history.operationType'),
                  dataIndex: 'action',
                  key: 'action',
                  render: (text) => (
                    <Tag color={text === 'replace' ? 'green' : 'blue'}>
                      { text === 'replace' ? t('history.replace') : t('history.manual') }
                    </Tag>
                  ),
                },
              ]}
              rowKey="timestamp"
              pagination={false}
              scroll={{ y: 400 }}
            />
          )}
        </Modal>
      </Content>

      <Footer style={{ textAlign: 'center' }}>
        ©2024 {t('footer.copyright')}
      </Footer>
    </Layout>
  );
}

export default HistoryPage;